<template>
  <Content
    class="col-span-full"
    :title="$route.name"
    type="list"
    :pagination="{
      count: tableData.count,
      current: +$route.query.p,
      request: 'rules/',
    }"
    @tableData="tableData = $event"
    @loading="loading = $event"
  >
    <el-table
      :empty-text="$t('NO_DATA')"
      :data="tableData.results"
      v-loading="loading"
      :stripe="true"
      style="width: 100%"
    >
      <el-table-column
        sortable
        type="index"
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('INDEX')"
        width="60"
      >
        <template slot-scope="scope">
          <div :data-title="$t('INDEX')">
            {{ scope.$index + 1 }}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('TITLE')"
        width="100"
      >
        <template slot-scope="scope">
          <div :data-title="$t('TITLE')" class="font-medium">
            {{ scope.row.title }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('VEHICLE_TITLE')"
        width="350"
      >
        <template slot-scope="scope">
          <div :data-title="$t('VEHICLE_TITLE')">
            <div class="grid xl:grid-cols-2 gap-1">
              <div
                v-for="vehicle in scope.row.company_vehicles"
                :key="vehicle.id"
              >
                <LicensePlate
                  :vehicle="vehicle.vehicle"
                  :license_plate="vehicle.license_plate"
                  :chars="alphaList"
                  size="small"
                />
              </div>
            </div>

            <div
              v-if="scope.row.company_vehicles.length > 2"
              class="text-xs text-gray-500"
            >
              +{{ scope.row.company_vehicles.length - 2 }} {{ $t('MORE') }}
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('TIME')"
        width="120"
      >
        <template slot-scope="scope">
          <div :data-title="$t('TIME')" class="text-sm">
            <div>{{ scope.row.start_time }}</div>
            <div class="text-gray-500">{{ $t('TO') }}</div>
            <div>{{ scope.row.end_time }}</div>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('METRICS')"
        width="120"
      >
        <template slot-scope="scope">
          <div :data-title="$t('METRICS')">
            <div
              v-for="condition in scope.row.rule_conditions"
              :key="condition.id"
              class="mb-1"
            >
              <span class="text-sm font-medium">
                {{
                  $i18n.locale == 'fa'
                    ? condition.metric.fa_title
                    : condition.metric.en_title
                }}
              </span>
              <span
                class="text-xs text-gray-500 ml-1"
                v-if="condition.metric.unit"
              >
                ({{ condition.metric.unit }})
              </span>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('CONDITION_RULES')"
        width="150"
      >
        <template slot-scope="scope">
          <div :data-title="$t('CONDITION_RULES')">
            <div
              v-for="condition in scope.row.rule_conditions"
              :key="condition.id"
              class="mb-1 text-sm gap-1 flex items-center"
            >
              <span class="font-mono text-xs bg-gray-100 rounded">
                {{
                  $i18n.locale == 'fa'
                    ? condition.operator.fa_title
                    : condition.operator.en_title
                }}
              </span>
              <span class="text-xs text-gray-500" v-if="condition.metric.unit">
                {{ condition.metric.unit }}
              </span>
              <span v-if="condition.metric.code !== 'fence'">
                {{ condition.threshold.value }}
              </span>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="" width="150">
        <template slot-scope="scope">
          <div class="fixed-column">
            <tableFeatures
              :description="scope.row.description"
              :router="{
                name: 'RULES_EDIT',
                id: scope.row.id,
              }"
              :title="$t('CONDITION_RULES')"
              @open="open($event)"
              @view="view($event, 'RULES_VIEW')"
              :view="true"
              @remove="remove($event)"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>
  </Content>
</template>

<script>
import { conditionsList, removeCondition } from '@/api/conditions'
import tableFeatures from '@/components/elements/tableFeatures'
import { licenseChars } from '@/api/global'
import LicensePlate from '@/components/cars/LicensePlate'
import { open } from '@/mixins/openDesc'
import { jalaliToGregorian } from '@/mixins/jalaliToGregorian'
import { view } from '@/mixins/viewPage'

export default {
  mixins: [open, jalaliToGregorian, view],
  data() {
    return {
      tableData: {
        results: [],
      },
      search: '',
      searchValue: '',
      selectedTags: '',
      searchLoading: false,
      loading: true,
      alphaList: [
        {
          code: '',
          char: '',
          id: '',
        },
      ],
    }
  },

  methods: {
    remove(id) {
      removeCondition.api(id).then((res) => {
        if (res) {
          this.$notify({
            title: this.$i18n.t('SOMETHING_DELETED', {
              type: this.$i18n.t('CONDITION_RULES'),
            }),
            duration: 4000,
            customClass: 'info_msg',
          })
          this.tableData.results = this.tableData.results.filter(
            (table) => table.id !== id
          )
        }
      })
    },
    searchService(val) {
      this.searchValue = val
      this.fetchData()
    },

    searchTagInService(val) {
      this.selectedTags = val.join(',')
      this.fetchData()
    },

    fetchData() {
      this.searchLoading = true
      this.loading = true

      conditionsList.api().then((res) => {
        if (res) {
          this.tableData = res
        }
        this.loading = false
        this.searchLoading = false
      })
    },

    sortprice(a, b) {
      if (a.price < b.price) return -1
      if (a.price > b.price) return 1
      return 0
    },
    loadInitialData() {
      this.fetchData()
    },
  },
  mounted() {
    this.loadInitialData()
    licenseChars.api().then((res) => {
      this.alphaList = res
    })
  },
  components: {
    tableFeatures,
    LicensePlate,
  },
}
</script>
